/* eslint-disable eqeqeq */
/**
 * Phase 6e2c-3 — the accessory selector LEFT PANEL (the 4 templates'
 * `.tab-pane.input-tab-pane` form body). One component switches on type to emit
 * the per-type field set (opening / damper / louver / hood), all sharing the
 * mounting-plane + plane-conditional position block (AccessoryPositionFields)
 * and the desired-face-velocity + auto-size + airflow/face-velocity/apd rows.
 *
 * The DOM (class names, input names, .model_field/.params_field/.position_field
 * delegation classes) matches the legacy templates so the selector's handlers +
 * the CSS hit identically.
 */

import type { ChangeEvent } from 'react';
import { airStreamOptions } from '../airStreamOptions';
import { useAirStreamOfferedIds } from '../useAirStreamOfferedIds';
import {
  eq,
  hoodMissingAngles,
  hoodShownAngles,
  type ParamsCascade,
  type PlaneRow,
} from './accessorySelectorData';
import { AccessoryPositionFields } from './AccessoryPositionFields';
import { SelectorNumberInput } from './selectorNumberInput';
import { cmsList, truncDec } from '../formKit';
import type { ComboFace, ComboFaceView, ComboSmartlistRow } from './comboLouver';

type AccType = 'damper' | 'louver' | 'hood' | 'opening';

/**
 * COMBO LOUVER (KA-5245) — the combo-only rows. A1.1 rendered them; A1.2/A1.3
 * wired the ones that drive the `aw_dims` option lists and the resize engine;
 * wave 3 (A1.4/A1.5/A1.6) wired the rest, so NOTHING here is inert any more.
 *
 * LIVE:
 *  · the Enclosure W × H × D inputs + the Material select (`model_field`s →
 *    `changeModelField`'s combo branch);
 *  · the relocated Casing Mount radios (the branch dev commits a092d3353c +
 *    56ff2152ec fixed);
 *  · the auto-size TOGGLE (`toggleComboAutoSize` — dev commit 3135165b0d's
 *    `maybeAutoSize().then(fetchAwDims)` ordering fix lives on it);
 *  · the *Smartlist* link (flips the mounting popup to the shared combo list),
 *    and the combo smartlist rows themselves (`ComboSmartlistRows`, below);
 *  · the three per-face W × H selects carry REAL options from
 *    `/louvers/aw_dims/`, their `.not-valid` flag and the restored-option fallback;
 *  · (A1.4) the three face CHECKBOXES → `toggleComboFace` :1832-1872, incl. the
 *    "last remaining checked face cannot be unchecked" guard, and the face selects
 *    → `onComboFaceFieldChange` :1131-1159 (the only sender of `edited_face`);
 *  · (A1.4) *Reset* → `resetComboFace` :1512-1541;
 *  · (A1.5) the *How to* link → the portalled `ComboLouverHowTo` popup.
 *
 * The SHARED rows a combo also uses (Tag, Manufacturer, Series, Frame Material,
 * Blade Orientation, Air Stream, Air Direction, Airflow, Desired FV, Mounting
 * Plane, the distance rows) keep the pre-existing louver wiring untouched.
 */
const NOOP = () => undefined;

interface LeftPanelProps {
  type: AccType;
  cms: Record<string, unknown>;
  /** the GLOBAL AHU cms doc (legacy self.globalCMS / fn['current.cms']) —
   * `air.air_stream` feeds the Air Stream select. The per-type `cms` doc
   * (opening/hoods/louvers `params/`) has NO `air_stream` key at all
   * (CMSAHUOpeningParamsViewSet etc. never return one), so reading it off
   * `cms` left the select with only its blank placeholder option — the same
   * class of bug already fixed for EvapPack/GasHeater/Coil (see their
   * `globalDoc` comments). */
  globalDoc?: Record<string, unknown> | null;
  model: Record<string, unknown>;
  params: Record<string, unknown>;
  position: Record<string, unknown>;
  plane: PlaneRow | undefined;
  planeValue: string | null;
  planesOnCabinet: PlaneRow[];
  internalWalls: Array<Record<string, unknown>>;
  awDims: { show_available_airway: boolean; aw_label?: string; aw_first_value?: unknown; aw_second_value?: unknown };
  bladeOrientations: Array<Record<string, unknown>> | null;
  smartlist: SmartlistState;
  onModelField: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onParamsField: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onPositionField: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onAutoSize: () => void;
  /** `_syncManufacturerSeriesFilters`' hide sets — see `SelectParams`. */
  paramsCascade: ParamsCascade;
  onSmartlistToggle: () => void;
  onSmartlistRow: (id: number) => void;
  onSmartlistReset: () => void;

  // ---- Combo Louver (KA-5245) — A1.1 -------------------------------------
  /** `combo_mode` (accessory.selector.js:127 / :361) — computed ONCE at init
   * from `staging.is_combo`; there is NO control anywhere that leaves combo mode
   * (inventory DEAD-1), so it never flips for the dialog's lifetime. */
  comboMode?: boolean;
  /** `combo_faces_view` — `_getComboFacesView` :923-956. */
  comboFacesView?: ComboFaceView[];
  /** `combo_autosize_enabled` :362 — DEFAULT ON for a combo. */
  comboAutoSizeEnabled?: boolean;
  /** `combo_louver_selected_count` / `combo_louver_total_count` :370-371. */
  comboCounts?: { count: number; total: number };
  /** `enclosure_materials` :372 — GET /ahu/louvers/<id>/enclosure_materials/. */
  enclosureMaterials?: Array<{ id: number; name: string }>;
  /** A1.3 — `toggleComboAutoSize` :1810-1830 (★ the ordering fix lives there). */
  onComboAutoSizeToggle?: (e: ChangeEvent<HTMLInputElement>) => void;
  /** A1.2 — `showComboSmartlist` :1576-1581 (flips the popup to the shared list). */
  onComboSmartlistToggle?: () => void;
  /** A1.4 — `toggleComboFace` :1832-1872. */
  onComboFaceToggle?: (face: ComboFace, checked: boolean) => void;
  /** A1.4 — `onComboFaceFieldChange` :1131-1159. */
  onComboFaceField?: (
    face: ComboFace,
    name: 'combo_aw_width' | 'combo_aw_height',
    raw: string
  ) => void;
  /** A1.4 — `resetComboFace` :1512-1541. */
  onComboReset?: () => void;
  /** A1.5 — `openComboLouverHowTo` :3374-3391 (toggles the portalled popup). */
  onComboHowTo?: () => void;
}

const list = (cms: Record<string, unknown>, key: string): Array<Record<string, unknown>> => {
  const v = cms[key];
  return Array.isArray(v) ? (v as Array<Record<string, unknown>>) : [];
};

/** hood.selector.tmpl:104 / :112 / :120 / :128 — each angle box's own bounds. */
const HOOD_ANGLE_LIMITS: Record<string, { min: number; max?: number }> = {
  angle_1: { min: 0 },
  angle_2: { min: 20, max: 135 },
  angle_3: { min: 20, max: 135 },
  angle_4: { min: 20, max: 135 },
};

const dec = (v: unknown): string => (v == null ? '' : String(v));
const trunc = truncDec;

// ---- shared rows -----------------------------------------------------------
/**
 * `inputRow` = the legacy `class="col-xs-7 input-part row"` shape (e.g.
 * hood.selector.tmpl:108). The grid class has to sit ON the input-part, not on a
 * div nested inside it: `.input-part` is `padding-left: 0` with the default 15px
 * right gutter (common.css:13908), and `.input-part.row` drops that gutter too
 * (:13914). A nested `.row` keeps the gutter and adds its own -15px margins, so
 * the columns hang 15px past the input-part on BOTH sides and stop lining up
 * with every other control in the dialog.
 */
const LabelRow = ({ label, unit, inputRow, children }: { label: string; unit?: string; inputRow?: boolean; children: React.ReactNode }) => (
  <div className="row">
    <div className="col-xs-5 label-part">
      <span>{label}</span>
      {unit && <span className="unit right">{unit}</span>}
    </div>
    <div className={`col-xs-7 input-part${inputRow ? ' row' : ''}`}>{children}</div>
  </div>
);

const SelectModel = ({ name, value, rows, labelKey, onChange, hideEmpty = true }: {
  name: string;
  value: unknown;
  rows: Array<Record<string, unknown>>;
  labelKey: string;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  hideEmpty?: boolean;
}) => (
  <select name={name} className="model_field" value={value == null ? '' : String(value)} onChange={onChange}>
    <option value="" className={hideEmpty ? 'hide' : undefined}>- -</option>
    {rows.map((r) => (
      <option key={String(r.id)} value={String(r.id)}>{String(r[labelKey] ?? '')}</option>
    ))}
  </select>
);

/**
 * `hiddenIds` is `_syncManufacturerSeriesFilters`' `toggleClass('hide', …)` pass
 * (`accessory.selector.js:753-757 / :792-796 / :830-834`). The select always
 * renders the FULL cms list in the order the server served it; the cascade only
 * marks the options outside the allowed set, exactly as legacy does — see
 * `syncManufacturerSeriesFilters` for why the mechanism (not just the resulting
 * set) is user-visible. Bootstrap's `.hide{display:none!important}` hides them.
 */
const SelectParams = ({ name, value, rows, labelKey, valueKey = 'id', onChange, hideEmpty = false, hiddenIds }: {
  name: string;
  value: unknown;
  rows: Array<Record<string, unknown>>;
  labelKey: string;
  valueKey?: string;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  hideEmpty?: boolean;
  hiddenIds?: Set<string>;
}) => (
  <select name={name} className="params_field" id={name === 'series_id' ? 'accessory_series_id' : undefined}
    value={value == null ? '' : String(value)} onChange={onChange}>
    <option value="" className={hideEmpty ? 'hide' : ''}>- -</option>
    {rows.map((r) => (
      <option key={String(r[valueKey])} value={String(r[valueKey])}
        className={hiddenIds?.has(String(r[valueKey])) ? 'hide' : undefined}>{String(r[labelKey] ?? '')}</option>
    ))}
  </select>
);

/**
 * ★ Every dialog number input now runs legacy's `validateNumberField` DISPLAY
 * pass (2 dp / rounded + thousands separators, re-applied by legacy on EVERY
 * `render()` over `input[data-type="number"], input[data-formatter_type="decimal"]`
 * — accessory.selector.js:406-410). React printed raw model values, so the two
 * stacks showed different text in the same box (`1200` vs `1,200`,
 * `51.93600000000001` vs `51.94`, `0.2392` vs `0.24`, …). That divergence predates
 * the combo port and affects ALL FOUR accessory types, but wave 2 un-occluded the
 * right-hand column and made it visible. See `SelectorNumberInput` for the
 * mechanics — including how the formatter is kept out of the way while the user is
 * typing, and how wave 2's commit-on-blur semantics are preserved via `commitOn`.
 */
const NumberInput = ({ name, value, cls = 'model_field w-100', formatter = 'decimal', readOnly, disabled, min, max, onChange, dataTypeNumber, commitOn }: {
  name: string;
  value: unknown;
  cls?: string;
  formatter?: 'decimal' | 'integer';
  readOnly?: boolean;
  /** combo enclosure W/H while auto-size is ON (louver.selector.tmpl:268-269
   * `{{#if combo_autosize_enabled}}disabled tabindex="-1"{{/if}}`). */
  disabled?: boolean;
  min?: unknown;
  max?: unknown;
  /** legacy's read-only FV / APD boxes carry `data-formatter_type` only (no
   * `data-type="number"`) — tmpl:240 / :251. */
  dataTypeNumber?: boolean;
  /** 'blur' = the combo enclosure trio (see SelectorNumberInput). */
  commitOn?: 'change' | 'blur';
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}) => (
  <SelectorNumberInput
    name={name}
    value={value}
    className={cls}
    formatter={formatter}
    readOnly={readOnly}
    disabled={disabled}
    min={min}
    max={max}
    dataTypeNumber={dataTypeNumber}
    commitOn={commitOn}
    onChange={onChange}
  />
);

const FaceVelBlock = ({ type, model, onModelField, onAutoSize, comboMode, comboAutoSizeEnabled, onComboAutoSizeToggle }: {
  type: AccType;
  model: Record<string, unknown>;
  onModelField: (e: ChangeEvent<HTMLInputElement>) => void;
  onAutoSize: () => void;
  comboMode?: boolean;
  comboAutoSizeEnabled?: boolean;
  onComboAutoSizeToggle?: (e: ChangeEvent<HTMLInputElement>) => void;
}) => {
  const fvName = type === 'hood' ? 'face_velocity' : 'face_velocity_actual';
  const ratio = type === 'hood' ? '3:1' : '2:1';
  const cap = type.charAt(0).toUpperCase() + type.slice(1);
  // Combo Louver (KA-5245) — A1.1, the auto-size BUTTON deltas at
  // louver.selector.tmpl:230.
  //  · the `tooltip` attribute is wrapped in `{{#unless combo_mode}}` — a combo
  //    has NO auto-size tooltip (the How-to popup documents the behaviour).
  //  · the inline style is LOUVER-only but NOT combo-only: greyed + click-through
  //    off while auto-size is ON, restored when it is OFF. Legacy adds
  //    `!important` to beat `.combo-autosize-toggle-label + .auto-size {
  //    pointer-events: none }` (rating_mode.scss:10344-10349); an inline style
  //    already outranks any class selector, so the COMPUTED values match without
  //    it (React cannot emit !important through the `style` prop).
  //
  //    ★ The `{{#if combo_autosize_enabled}}` that picks between the two styles
  //    is NOT gated on `combo_mode`, and `_comboAutoSizeEnabled`
  //    (accessory.selector.js:144-146) is assigned for EVERY louver, not just a
  //    combo: `staging_model.has('auto_size') ? !!get('auto_size') : true`.
  //    `AhuAccessoryLouver.auto_size` is a real column with `default=True`
  //    (ahu/models_accessory_louver.py:43) and is serialized
  //    (api/rest_ahu/louver_serializers.py:61,93), so a PLAIN louver takes the
  //    ON branch too — which is why dev renders its auto-size button grey
  //    (#757575) and inert, and dev3 rendered it live blue. Only louver.selector
  //    .tmpl:230 carries the style attribute; damper/hood/opening (:208/:215/:98)
  //    have none, so they keep the stylesheet's blue.
  const autoSizeTooltip = comboMode
    ? {}
    : {
        tooltip: `${cap} will be automatically sized based on ${ratio} width x height ratio, airflow and desired velocity`,
      };
  const autoSizeStyle: React.CSSProperties | undefined = type !== 'louver'
    ? undefined
    : comboAutoSizeEnabled
      ? { paddingLeft: 5, marginLeft: 0, color: '#757575', pointerEvents: 'none' }
      : { paddingLeft: 5, marginLeft: 0, pointerEvents: 'auto', cursor: 'pointer' };
  return (
    <>
      <div className="row">
        <div className="col-xs-5 label-part"><span>Desired Face Velocity</span><span className="unit right">fpm</span></div>
        <div className="col-xs-7 input-part with-checkbox">
          <NumberInput name="desired_face_velocity_actual" value={model.desired_face_velocity_actual} formatter="integer" min={0} onChange={onModelField} />
          {/* Combo Louver (KA-5245) — A1.1. louver.selector.tmpl:229: the
              auto-size TOGGLE sits between the FV input and the auto-size
              button, combo-only, and defaults CHECKED (inventory §2.2 row 15).
              LIVE from A1.3 — `toggleComboAutoSize` :1810-1830 is where dev
              commit 3135165b0d's ordering fix lives:
                ON  → `_maybeComboAutoSize().then(() => _fetchComboAwDims())`
                OFF → `_fetchComboAwDims()` immediately, no /with_position at all.
              */}
          {comboMode && (
            <label className="combo-autosize-toggle-label">
              <input
                type="checkbox"
                className="combo-autosize-toggle"
                checked={!!comboAutoSizeEnabled}
                onChange={onComboAutoSizeToggle ?? NOOP}
              />
            </label>
          )}
          {/* Combo Louver (KA-5245) — A1.1: `ka-has-tooltip` is dropped in combo
              mode because the `tooltip` ATTRIBUTE is dropped there (see
              autoSizeTooltip above, tmpl:230 `{{#unless combo_mode}}tooltip=…`).
              Without this, `.ka-has-tooltip:hover:after { content: attr(tooltip) }`
              (ui.tooltip.scss:68-73) resolves to "" and — because
              `.ka-has-tooltip.ka-tooltip-left.auto-size:after` sets
              `min-width:200px` (:54-66) over `background:#8d8d8d` — paints an
              EMPTY 200px grey box on hover. Legacy never emits the class here
              (it is gated on `{{#unless model.airflow_actual}}`, and a combo's
              airflow is always set); measured legacy class is
              `"btn auto-size  ka-tooltip-left "`. Masked today by
              `pointer-events:none` while auto-size is ON — it would surface the
              moment A1.4 wires the toggle. Non-combo is left byte-identical. */}
          <button type="button"
            className={`btn auto-size${comboMode ? '' : ' ka-has-tooltip'} ka-tooltip-left${model.airflow_actual ? '' : ' non-clickable'}`}
            {...autoSizeTooltip}
            style={autoSizeStyle}
            onClick={(e) => { e.preventDefault(); if (model.airflow_actual) onAutoSize(); }}>
            auto-size
          </button>
        </div>
      </div>
      {/* FACE VELOCITY is `data-formatter_type="decimal" data-step="0.01"` in ALL
          FOUR templates (louver.selector.tmpl:240 and the damper / opening / hood
          equivalents), so legacy's render-time `validateNumberField` pass
          reformats it everywhere — its selector matches EITHER
          `data-type="number"` OR `data-formatter_type="decimal"`. Measured legacy
          vs React before this wave: `51.94` vs `51.93600000000001`.

          ★ APD IS *NOT* UNIFORM ACROSS THE FOUR TEMPLATES — do not treat it as if
          it were. Only two carry the formatter attributes:
            louver.selector.tmpl:251  data-formatter_type="decimal" data-step="0.01"
            hood.selector.tmpl:236    same + data-type="number"
          The other two are bare, with NO formatter attributes at all:
            damper.selector.tmpl:229  <input name="apd" type="text" class="w-100"
                                        readonly value="{{model.apd}}" tabindex="-1">
            opening.selector.tmpl:119 identical
          so legacy's pass SKIPS them and prints the RAW model value. An apd of
          `0.0069` therefore reads `0.0069` on a damper/opening but `0.01` on a
          hood/louver. Formatting all four would silently normalise two of them —
          a divergence in the "React quietly fixes legacy" direction. Parity-only:
          format exactly where legacy formats. */}
      <LabelRow label="Face Velocity" unit="fpm"><NumberInput name={fvName} value={model[fvName]} cls="w-100" readOnly dataTypeNumber={false} /></LabelRow>
      <LabelRow label="Air Pressure Drop" unit="in WC">
        {type === 'louver' || type === 'hood' ? (
          <NumberInput name="apd" value={model.apd} cls="w-100" readOnly dataTypeNumber={false} />
        ) : (
          <input name="apd" type="text" className="w-100" readOnly tabIndex={-1} value={dec(model.apd)} onChange={() => undefined} />
        )}
      </LabelRow>
    </>
  );
};

const AvailableAirway = ({ awDims }: { awDims: LeftPanelProps['awDims'] }) =>
  awDims.show_available_airway ? (
    <div className="row">
      <div className="col-xs-5 label-part"><span>{awDims.aw_label}</span><span className="unit right">in</span></div>
      <div className="col-xs-7 input-part row">
        <div className="col-xs-6"><NumberInput name="aw_width" value={awDims.aw_first_value} cls="" readOnly /></div>
        <div className="col-xs-6"><NumberInput name="aw_height" value={awDims.aw_second_value} cls="" readOnly /></div>
      </div>
    </div>
  ) : null;

// ---- Combo Louver (KA-5245) rows — A1.1 ------------------------------------

/** louver.selector.tmpl:18-26 — the combo-only *How to* link, FIRST row of the
 * left column (it replaces the plain louver's Smartlist counter row, :6-16).
 * LIVE from A1.5: `openComboLouverHowTo` :3374-3391 TOGGLES the popup (clicking
 * again while it is `.active` closes it), which is `ComboLouverHowTo.tsx`.
 * DEVIATION: legacy's `<a href="javascript:void(0)">`; React drops the href
 * (React 18 warns on `javascript:` URLs) exactly as the two prior how-to ports
 * did (FilterSelector.tsx:1185, FanSelector.tsx:2554). `.combo-louver-how-to-menu
 * a` already sets `cursor: pointer` (rating_mode.scss:10406-10415). */
const ComboHowToRow = ({ onHowTo }: { onHowTo?: () => void }) => (
  <div className="row combo-louver-how-to-row">
    <div className="col-xs-12">
      <span className="combo-louver-how-to-menu" data-name="how_to" onClick={onHowTo ?? NOOP}>
        <a>How to</a>
      </span>
    </div>
  </div>
);

/** louver.selector.tmpl:34-47 — "Louvers x / y · Reset · Smartlist".
 * ONE Reset and ONE Smartlist for the whole combo: both act on the SHARED
 * manufacturer / series / blade orientation / frame material (common to all three
 * faces); airway sizes are the per-face dropdowns below.
 * ⚠ Q14: the counter is `matches / total` over `cms.combo_smartlist`, so x always
 * equals the filtered row count — 43/43 on open, 1/43 after a pick.
 * Smartlist is LIVE from A1.2 (`showComboSmartlist` :1576-1581 flips the mounting
 * popup to `data-tab="smartlist"`); Reset is LIVE from A1.4 (`resetComboFace`
 * :1512-1541 — it clears the shared identity, every face's airway pair and the
 * enclosure W/H, and ⚠ deliberately leaves APD stale + makes the next `aw_dims`
 * 500 on the literal `"null"`). */
const ComboResetRow = ({ counts, onSmartlist, onReset }: {
  counts: { count: number; total: number };
  onSmartlist?: () => void;
  onReset?: () => void;
}) => (
  <div className="row combo-reset-row">
    <div className="col-xs-5 label-part">
      <span className="bold-label">Louvers</span>
    </div>
    <div className="col-xs-7">
      <span className="combo-louver-count">
        <span className={`combo-louver-count-selected${counts.count ? '' : ' warning'}`}>
          {counts.count}
        </span>
        {' / '}
        <span className="combo-louver-count-total">{counts.total}</span>
      </span>
      <span className="icon-refresh-1 reset-btn combo-louver-reset" onClick={onReset ?? NOOP}>Reset</span>
      <span
        className="combo-louver-smartlist"
        title="Pick manufacturer / series / blade orientation / frame material from the catalog"
        onClick={onSmartlist ?? NOOP}
      >
        Smartlist
      </span>
    </div>
  </div>
);

/** louver.selector.tmpl:145-170 — the three face rows (Front / Left Side / Right
 * Side "Airway W x H"): a checkbox plus two <select>s separated by an `x`.
 *  · the FIRST row carries `style="margin-top: 15px"` (`{{#unless @index}}`);
 *  · `checkbox_disabled` = the last remaining checked face can't be unchecked;
 *  · `inputs_disabled` = checked AND auto-size OFF is required to edit W x H;
 *  · the option lists come from `/louvers/aw_dims/` — LIVE from A1.2. Empty until
 *    the shared identity is fully picked (mfg + series + blade orientation + frame
 *    material), which is also what legacy shows for a fresh combo;
 *  · option labels use this file's `trunc` (toFixed(2)) rather than legacy's
 *    `truncate_decimal` (Math.trunc(n*100)/100 + toFixed(2)) — identical for every
 *    measured aw_dims value (they arrive with one decimal, e.g. 12.0 / 62.0 /
 *    288.0) and consistent with the plain-louver aw options already in this file;
 *  · `aw_*_extra` is `_restoreMissingSelectOption` (:602-619) — a saved size the
 *    current option list no longer offers comes back as a selected+DISABLED
 *    option instead of being silently lost;
 *  · `.not-valid` on both selects is `_setComboFaceInvalid` (:1718-1725) — this
 *    face's fully-specified params matched no catalog louver.
 * ⚠ Q2 lives here visibly: after a smartlist pick at depth 12 the `&side=true`
 * list comes back EMPTY (it was fetched pre-resize), so both side selects render
 * blank even though the faces DO hold 22 × 62 from the server. ⚠⚠ And Q8 lets
 * `#select` go clickable in exactly that state (`_comboFacesSized` only needs
 * `.some()` while auto-size is ON) — both mirrored, neither fixed.
 * LIVE from A1.4: the checkbox is `toggleComboFace` :1832-1872 (the last remaining
 * checked face is guarded BOTH by `checkbox_disabled` here and by the handler), and
 * each select is `onComboFaceFieldChange` :1131-1159 — the ONLY sender of
 * `edited_face`, i.e. the only thing that lets the server grow a dimension. */
const ComboFaceRows = ({ facesView, onToggle, onField }: {
  facesView: ComboFaceView[];
  onToggle?: (face: ComboFace, checked: boolean) => void;
  onField?: (face: ComboFace, name: 'combo_aw_width' | 'combo_aw_height', raw: string) => void;
}) => (
  <>
    {facesView.map((f, index) => {
      const selectCls = `combo-face-field combo-aw-select${f.not_valid ? ' not-valid' : ''}`;
      return (
        <div
          key={f.face}
          className="row combo-row combo-face-row"
          data-face={f.face}
          style={index === 0 ? { marginTop: 15 } : undefined}
        >
          <div className="col-xs-5 label-part">
            <label className="checkbox combo-face-check-label">
              <span>{f.label} Airway W x H</span>
            </label>
          </div>
          <div className="col-xs-7 input-part combo-face-controls">
            <input
              type="checkbox"
              className="combo-face-checkbox"
              data-face={f.face}
              checked={f.checked}
              disabled={f.checkbox_disabled}
              onChange={(e) => onToggle?.(f.face, e.target.checked)}
            />
            <select
              name="combo_aw_width"
              data-face={f.face}
              className={selectCls}
              disabled={f.inputs_disabled}
              tabIndex={f.inputs_disabled ? -1 : undefined}
              value={f.aw_width === '' ? '' : String(f.aw_width)}
              onChange={(e) => onField?.(f.face, 'combo_aw_width', e.target.value)}
            >
              <option value="">- -</option>
              {f.aw_widths.map((w) => (
                <option key={String(w)} value={String(w)}>{trunc(w)}</option>
              ))}
              {f.aw_width_extra && (
                <option value={f.aw_width_extra.value} disabled>{f.aw_width_extra.label}</option>
              )}
            </select>
            <span className="combo-x">x</span>
            <select
              name="combo_aw_height"
              data-face={f.face}
              className={selectCls}
              disabled={f.inputs_disabled}
              tabIndex={f.inputs_disabled ? -1 : undefined}
              value={f.aw_height === '' ? '' : String(f.aw_height)}
              onChange={(e) => onField?.(f.face, 'combo_aw_height', e.target.value)}
            >
              <option value="">- -</option>
              {f.aw_heights.map((h) => (
                <option key={String(h)} value={String(h)}>{trunc(h)}</option>
              ))}
              {f.aw_height_extra && (
                <option value={f.aw_height_extra.value} disabled>{f.aw_height_extra.label}</option>
              )}
            </select>
          </div>
        </div>
      );
    })}
  </>
);

/** louver.selector.tmpl:171-181 — Casing Mount, RELOCATED into the left column
 * for a combo (the plain louver renders it in the position column under
 * `{{#if position.mounting_plane_id}}{{#unless combo_mode}}`, :297-319). Note the
 * combo copy is NOT gated on a mounting plane and has no `in` unit span.
 * LIVE from A1.3 — `changeModelField`'s casing_mount branch (:2408-2420) calls
 * BOTH `_fetchComboAwDims()` and `_maybeComboAutoSize()`/`_comboRecalc()`; the
 * second half is dev commit 56ff2152ec, without which the enclosure never resizes
 * at all while auto-size is off.
 * ⚠ Q3/Q4 live on that path and are MIRRORED: the aw_dims refetch precedes the
 * resize (lists bounded by the old width), and Recess → Surface never shrinks
 * `enclosure_width` back (the box is left 12 in too wide). */
const ComboCasingMountRow = ({ rows, current, onModelField }: {
  rows: Array<Record<string, unknown>>;
  current: unknown;
  onModelField: (e: ChangeEvent<HTMLInputElement>) => void;
}) => (
  <div className="row combo-row">
    <div className="col-xs-5 label-part"><span>Casing Mount</span></div>
    <div className="col-xs-7 input-part">
      <div className="checkboxes-group">
        {rows.map((r) => (
          <label className="radio" key={String(r.id)}>
            <input type="radio" name="casing_mount_id" value={String(r.id)}
              checked={eq(r.id, current)} className="model_field" onChange={onModelField} />
            <span>{String(r.name ?? '')}</span>
          </label>
        ))}
      </div>
    </div>
  </div>
);

/** louver.selector.tmpl:259-284 — the combo-only ENCLOSURE block at the top of
 * the position column: a bold section heading, the W x H x D triple and the
 * Material select.
 *  · W and H are DISABLED while auto-size is on; D is ALWAYS enabled (the depth is
 *    the one dimension the sides-first engine takes as an input, inventory §5);
 *  · `data-min` falls back through `get_one` to 18 / 12 / 12 and `data-max` comes
 *    straight from CMS — every `combo_*` limit is NULL on dev3 (inventory R7), so
 *    the maxes render as empty attributes exactly as legacy does;
 *  · the material list is the AHU's casing sheet metals (9 rows on dev3, default
 *    697 = the tunnel casing outer skin).
 * LIVE from A1.3: all four are `model_field`s driven by `changeModelField`
 * :2380-2407 —
 *   D          → `_fetchComboAwDims()` then auto-size (ON) / `_comboRecalc()` (OFF);
 *   W and H    → `_fetchComboAwDims()` then `_comboRecalc()` ONLY, never auto-size
 *                (it would overwrite the value just typed);
 *   Material   → falls to the generic `else` → `_maybeComboAutoSize()`, and caches
 *                material_id/material_name into louver_params (:2344-2355).
 * ⚠ Q10: in legacy ONE enclosure edit yields 2-3 round trips because
 * `validateNumberField` reformats ("30" → "30.00" → "30" with separators) and
 * `render()` re-fires the handler. React's inputs format declaratively, so a
 * single edit produces ONE pair — see DEVIATIONS.
 * ⚠ Q15: the CMS clamp stays SILENT or it loops forever (:2970-2981). */
const ComboEnclosureBlock = ({ cms, model, materials, autoSize, onModelField }: {
  cms: Record<string, unknown>;
  model: Record<string, unknown>;
  materials: Array<{ id: number; name: string }>;
  autoSize: boolean;
  onModelField: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
}) => (
  <>
      <div className="row combo-row">
        <div className="col-xs-5 label-part">
          <span className="bold-label">Enclosure</span>
        </div>
      </div>
      <div className="row combo-row">
        <div className="col-xs-5 label-part"><span>Width x Height x Depth</span><span className="unit right">in</span></div>
        <div className="col-xs-7 input-part row">
          {/* legacy renders the RAW value then `validateNumberField` reformats
              every `input[data-type=number]` on each render (:406-410) to
              toFixed(2) + thousands separators; `SelectorNumberInput` is that
              formatter (measured board row: 18.00 × 64.71 × 12.00; a depth of 2430
              displays as 2,430.00), so the RAW model value is handed over here.
              commit-on-blur = legacy's `change .model_field` (its keyup binding is
              commented out) — see SelectorNumberInput's `commitOn`. */}
          <div className="col-xs-4">
            <NumberInput name="enclosure_width" value={model.enclosure_width} cls="model_field" commitOn="blur"
              min={cms.combo_louver_enclosure_width_min || 18}
              max={cms.combo_louver_enclosure_width_max ?? ''} disabled={autoSize}
              onChange={onModelField} />
          </div>
          <div className="col-xs-4">
            <NumberInput name="enclosure_height" value={model.enclosure_height} cls="model_field" commitOn="blur"
              min={cms.combo_louver_enclosure_height_min || 12}
              max={cms.combo_louver_enclosure_height_max ?? ''} disabled={autoSize}
              onChange={onModelField} />
          </div>
          <div className="col-xs-4">
            <NumberInput name="enclosure_depth" value={model.enclosure_depth} cls="w-100 model_field" commitOn="blur"
              min={cms.combo_min_depth || 12} max={cms.combo_max_depth ?? ''}
              onChange={onModelField} />
          </div>
        </div>
      </div>
      <div className="row combo-row">
        <div className="col-xs-5 label-part"><span>Material</span></div>
        <div className="col-xs-7 input-part">
          <select name="enclosure_material_id" className="model_field"
            style={{ maxWidth: 'calc(100% - 135px)' }}
            value={model.enclosure_material_id == null ? '' : String(model.enclosure_material_id)}
            onChange={onModelField}>
            <option value="">- -</option>
            {materials.map((m) => (
              <option key={String(m.id)} value={String(m.id)}>{String(m.name ?? '')}</option>
            ))}
          </select>
        </div>
      </div>
  </>
);

const MountingPlaneSelect = ({ type, model, position, planesOnCabinet, internalWalls, onPositionField }: {
  type: AccType;
  model: Record<string, unknown>;
  position: Record<string, unknown>;
  planesOnCabinet: PlaneRow[];
  internalWalls: Array<Record<string, unknown>>;
  onPositionField: (e: ChangeEvent<HTMLSelectElement>) => void;
}) => {
  // damper stamps hood_id / louver_id onto the selected value; controlled value
  // mirrors whichever it has.
  const selValue =
    type === 'damper' && (model.hood_id || model.louver_id)
      ? model.hood_id || model.louver_id
      : position.mounting_plane_id;
  return (
    <LabelRow label="Mounting Plane">
      <select name="mounting_plane_id" className="position_field w-100" required
        value={selValue == null ? 'None' : String(selValue)} onChange={onPositionField}>
        <option value="None" className="hide">- -</option>
        {/* `disabled` marks a plane the accessory is BUILT on that its section
            no longer offers — a floor in a section whose Floor Deck has gone
            (`AHUAccessoryDamper.mounting_planes`). Shown, never selectable. */}
        {planesOnCabinet.map((p) => (
          <option key={`c${p.id}`} value={String(p.id)} data-position="on_cabinet" data-value={String(p.value ?? '')}
            disabled={!!(p as { disabled?: boolean }).disabled}>
            {String(p.name ?? '')}
          </option>
        ))}
        {internalWalls.map((w) => (
          <option key={`iw${w.id}`} value={String(w.id)} data-position="on_internal_wall" data-value={String((w as any).value ?? '')}>
            {String((w as any).name ?? '')}
          </option>
        ))}
      </select>
    </LabelRow>
  );
};

// ---- the body --------------------------------------------------------------
export const AccessoryLeftPanel = (props: LeftPanelProps) => {
  const { type, cms, globalDoc, model, params, position, plane, planeValue, planesOnCabinet, internalWalls, awDims,
    bladeOrientations, smartlist, onModelField, onParamsField, onPositionField, onAutoSize } = props;

  // ★ Offered by this AHU's construction class, plus whatever the record
  // already holds -- an air stream is never cleared from a component or
  // accessory when its class withdraws it, so the dialog has to be able
  // to show its own saved value. Same rule and same helper as the inline
  // accessory forms; `globalDoc` names the streams, the ids say which may
  // be chosen. Declared here, above every early return in this component.
  const airStream = airStreamOptions(
    cmsList(globalDoc as Record<string, unknown> | undefined, 'air.air_stream'),
    useAirStreamOfferedIds(),
    model.air_stream_id as number | string | null | undefined
  );
  const planeKind = (plane?.plane as string) ?? null;
  // Combo Louver (KA-5245) — A1.1. `combo_mode` is `accessoryType === 'louver' &&
  // is_combo` (accessory.selector.js:127); it can only ever be true for a louver
  // and it NEVER flips (DEAD-1: no control leaves combo mode).
  const comboMode = type === 'louver' && !!props.comboMode;
  const comboFacesView = props.comboFacesView ?? [];
  const comboAutoSizeEnabled = !!props.comboAutoSizeEnabled;
  const comboCounts = props.comboCounts ?? { count: 0, total: 0 };

  // The top margin of the two BOTTOM columns — the Air Stream column
  // (`col-xs-5`) and the position column (`col-xs-7`) — is per-type in legacy,
  // and the two always carry the SAME value so the pair starts on one baseline:
  // opening 27px (opening.selector.tmpl:63 / :125), damper 20px
  // (damper.selector.tmpl:165 / :235), hood 20px (hood.selector.tmpl:162 /
  // :242), louver 13px (louver.selector.tmpl:186 / :257).
  //
  // The port generalised `opening`'s values onto all four. The position column
  // was corrected first; the Air Stream column stayed hardcoded at 20, which
  // left a louver's Air Stream row and everything under it 7px below Mounting
  // Plane and 7px below where legacy draws it. One constant now feeds both.
  //
  // Whether the position column carries `set-min-height` is per-type too: only
  // `opening` does. `set-min-height` (common.css:14012, min-height: calc(100% -
  // 260px)) belongs to the empty canvas placeholder below; on this column it is
  // a second, unwanted copy for every other type.
  const columnTopMargin = type === 'opening' ? 27 : type === 'louver' ? 13 : 20;
  const positionBlock = (
    <div className={`col-xs-7${type === 'opening' ? ' set-min-height' : ''}`}
      style={{ marginTop: columnTopMargin }}>
      {/* tmpl:259-284 — the enclosure block sits ABOVE Mounting Plane. */}
      {comboMode && (
        <ComboEnclosureBlock cms={cms} model={model} autoSize={comboAutoSizeEnabled}
          materials={props.enclosureMaterials ?? []} onModelField={onModelField} />
      )}
      <MountingPlaneSelect type={type} model={model} position={position}
        planesOnCabinet={planesOnCabinet} internalWalls={internalWalls} onPositionField={onPositionField} />
      {/* casing_mount — damper.selector.tmpl:256-273 (plane-9 restricted) and
          louver.selector.tmpl:297-319. The louver row was never ported; legacy
          renders it for every NON-COMBO louver once a mounting plane is picked,
          straight after Mounting Plane and before the distance rows, over the
          FULL `cms.casing_mount` list (no plane-9 narrowing — that filter is
          damper-only, damper.selector.tmpl:264-266). A combo louver keeps its own
          copy relocated into the left column (`ComboCasingMountRow`, tmpl:171-181),
          hence the `!comboMode` here. */}
      {(type === 'damper' || (type === 'louver' && !comboMode)) && position.mounting_plane_id != null && (
        <LabelRow label="Casing Mount" unit="in">
          <div className="checkboxes-group">
            {list(cms, 'casing_mount')
              .filter((r) => type !== 'damper' || !(String(position.mounting_plane_id) === '9') || String(r.id) === '1')
              .map((r) => (
                <label className="radio" key={String(r.id)}>
                  <input type="radio" name="casing_mount_id" value={String(r.id)} className="model_field"
                    checked={eq(r.id, model.casing_mount_id)} onChange={onModelField} />
                  <span>{String(r.name ?? '')}</span>
                </label>
              ))}
          </div>
        </LabelRow>
      )}
      <AccessoryPositionFields position={position} model={model} planeValue={planeValue} planeKind={planeKind} accessoryType={type} onField={onPositionField} />
      {/* blade direction (damper/louver).
          Combo Louver — tmpl:603-619 wraps this row in `{{#unless combo_mode}}`:
          a combo has NO user-selectable blade direction (it is DERIVED — the
          faces' direction follows air_direction_id × the mounting plane, see
          inventory §6.4), and `blade_direction_id` stays null on the parent
          (measured POST response, §3.2). Air Direction itself is NOT gated and
          stays editable (tmpl:199-209 / §2.2 row 13). */}
      {(type === 'damper' || (type === 'louver' && !comboMode)) && position.mounting_plane_id != null && (
        <LabelRow label="Blade Direction">
          {/* ★ `damper.selector.tmpl:635` / `louver.selector.tmpl:606` are a bare
              `<select name="blade_direction_id" class="model_field">` — editable,
              and styled like every other select in the dialog. The port added
              `non-clickable` (common.css:29093 `pointer-events: none; opacity:
              .5`) plus an inline `background: none` that also killed the caret
              sprite, so the row read as static text. */}
          <select name="blade_direction_id" className="model_field"
            value={model.blade_direction_id == null ? '' : String(model.blade_direction_id)} onChange={onModelField}>
            <option value="" className="hide">- -</option>
            {(() => {
              // `disabled` marks a direction the louver is BUILT with that its
              // catalogue orientation no longer offers (models_accessory_louver
              // `blade_directions`): shown, never selectable.
              const bd = (model.blade_directions as Record<string, any[]> | undefined) ?? {};
              const rows = bd[String(position.mounting_plane_id)] ?? [];
              return rows.map((r) => (
                <option key={String(r.id)} value={String(r.id)} disabled={!!r.disabled}>
                  {String(r.name ?? '')}
                </option>
              ));
            })()}
          </select>
        </LabelRow>
      )}
    </div>
  );

  // smartlist count row (damper/louver only).
  // Combo Louver — tmpl:6-16 wraps it in `{{#unless combo_mode}}`: the per-PART
  // catalog counter is meaningless for a combo (there is no single
  // `master_louver_id`); the "Louvers x / y" row replaces it.
  const smartlistRow = (type === 'damper' || (type === 'louver' && !comboMode)) && (
    <div className="row smartlist-count-row">
      <div className="col-xs-12">
        <button className={`btn btn-sync vis-${type}${!smartlist.count ? ' warning' : ''} clickable smartlist-toggle-btn`}
          onClick={props.onSmartlistToggle}>
          <span className="search-result">
            {/* `!= null` only: 0 is an answer (the filter matches nothing), and
                the slot stays blank only while there is no answer yet — the
                same rule as the evap-pack and VFD badges. */}
            {smartlist.count != null ? smartlist.count : '    '} / {smartlist.cmsCount ?? ''}
          </span>
          <i className="icon-list btn-icon-sync">Smartlist</i>
        </button>
        <span className="icon-refresh-1 reset-btn smartlist-reset" style={{ marginLeft: 8 }} onClick={props.onSmartlistReset}>Reset</span>
      </div>
    </div>
  );

  // === per-type field columns ===
  let leftCol1: React.ReactNode = null;
  let rightCol: React.ReactNode = null;

  if (type === 'opening') {
    leftCol1 = (
      <div className="col-xs-5" style={{ marginTop: 20 }}>
        <div className="row tag-row"><div className="col-xs-5 label-part"><span>Tag</span></div>
          <div className="col-xs-7 input-part"><input name="tag" type="text" className="model_field w-100" maxLength={36} value={dec(model.tag)} onChange={onModelField} /></div></div>
        <LabelRow label="Shape"><SelectModel name="shape_id" value={model.shape_id} rows={list(cms, 'shape')} labelKey="name" onChange={onModelField} /></LabelRow>
        <AvailableAirway awDims={awDims} />
        <div className="row"><div className="col-xs-5 label-part"><span>Selected Airway W x H</span><span className="unit right">in</span></div>
          <div className="col-xs-7 input-part row">
            <div className="col-xs-6"><NumberInput name="width" value={model.width} min={cms.min_width} max={cms.max_width} onChange={onModelField} /></div>
            <div className="col-xs-6"><NumberInput name="height" value={model.height} min={cms.min_height} max={cms.max_height} onChange={onModelField} /></div>
          </div></div>
      </div>
    );
    rightCol = (
      <div className="col-xs-5" style={{ marginTop: columnTopMargin }}>
        <LabelRow label="Air Stream"><SelectModel name="air_stream_id" value={model.air_stream_id} rows={airStream} labelKey="name" onChange={onModelField} hideEmpty={false} /></LabelRow>
        <LabelRow label="Airflow" unit="CFM"><NumberInput name="airflow_actual" value={model.airflow_actual} formatter="integer" min={0} max={400000} onChange={onModelField} /></LabelRow>
        <FaceVelBlock type={type} model={model} onModelField={onModelField} onAutoSize={onAutoSize} />
      </div>
    );
  } else if (type === 'hood') {
    // #2161 — the empty angle boxes, by name (see the Angle row below).
    const missingAngles = new Set(hoodMissingAngles(model));
    // Keep each box's own `data-min`/`data-max`: tmpl:104 gives angle_1 a
    // floor of 0 and no ceiling, tmpl:112/120/128 give the other three 20/135.
    const angleCls = (name: string) =>
      `model_field w-100${missingAngles.has(name) ? ' missing-value' : ''}`;
    leftCol1 = (
      <div className="col-xs-5" style={{ marginTop: 20 }}>
        <div className="row tag-row"><div className="col-xs-5 label-part"><span>Tag</span></div>
          <div className="col-xs-7 input-part"><input name="tag" type="text" className="model_field w-100" maxLength={36} value={dec(model.tag)} onChange={onModelField} /></div></div>
        <LabelRow label="Style"><SelectModel name="style_id" value={model.style_id} rows={list(cms, 'style')} labelKey="name" onChange={onModelField} /></LabelRow>
        <AvailableAirway awDims={awDims} />
        {/* tmpl:43 — the label picks up "/ H2" under the SAME condition that
            renders the third box (`hood_4_sided` / `hood_5_sided`, i.e. style_id
            4/5). React had ported the condition onto the input only, so the
            header read "Selected Airway W x H" while three boxes showed. */}
        <div className="row"><div className="col-xs-5 label-part">
          <span>Selected Airway W x H{(String(model.style_id) === '4' || String(model.style_id) === '5') ? ' / H2' : ''}</span>
          <span className="unit right">in</span></div>
          <div className="col-xs-7 input-part row">
            <div className="col-xs-4"><NumberInput name="width" value={model.width} min={0} onChange={onModelField} /></div>
            <div className="col-xs-4"><NumberInput name="height" value={model.height} min={0} onChange={onModelField} /></div>
            {(String(model.style_id) === '4' || String(model.style_id) === '5') && (
              <div className="col-xs-4"><NumberInput name="height_2" value={model.height_2} min={0} onChange={onModelField} /></div>
            )}
          </div></div>
        <LabelRow label="Depth Calculation Method"><SelectModel name="depth_mode_id" value={model.depth_mode_id} rows={list(cms, 'depth_mode')} labelKey="name" onChange={onModelField} /></LabelRow>
        <LabelRow label="Airflow Inlet Face">
          <select name="airflow_face_id" className="model_field" value={model.airflow_face_id == null ? '' : String(model.airflow_face_id)} onChange={onModelField}>
            {list(cms, 'airflow_face').filter((s) => eq((s.style as any)?.id, model.style_id))
              .flatMap((s) => ((s.airflow_face as Array<Record<string, unknown>>) ?? []))
              .map((o) => <option key={String(o.id)} value={String(o.id)}>{String(o.name ?? '')}</option>)}
          </select>
        </LabelRow>
        <LabelRow label="Depth" unit="in">
          <NumberInput name="depth" value={String(model.depth_mode_id) === '1' ? (model.depth ?? model.height) : model.depth}
            cls={`model_field w-100${String(model.depth_mode_id) === '1' ? ' non-clickable' : ''}`} onChange={onModelField} />
        </LabelRow>
        {/* tmpl:103-132 — the four angle boxes are columns of the input-part
            ITSELF (`col-xs-7 input-part row`), not of a nested row. Which of
            them a style shows is `hoodShownAngles` (angle_1 for every style,
            angle_2/3 for 3- and 4-sided, angle_4 for 4-sided): the SAME list
            the #select gate reads, so a box can never be required without
            being on screen. */}
        {/* #2161 — `angleCls` underlines an EMPTY angle box in red
            (`.missing-value`, rating_mode.scss), which is also what greys out
            #select (`isSelectValid` → `hoodMissingAngles`). Two ways to get
            one: the user clears the box, or the style's catalogue row has no
            `angle_<n>_default` and the style change had nothing to refill it
            with. Only the second survives a save — `save()` refills a cleared
            box from the default — and that is the one that leaves the whole
            AHU's next 3-D model failing in the CAD worker with nothing shown
            here. */}
        <LabelRow label="Angle - 1, 2, 3, 4" unit="in" inputRow>
          {hoodShownAngles(model).map((name) => (
            <div key={name} className="col-xs-3" style={{ paddingRight: 0 }}>
              <NumberInput name={name} value={model[name]} cls={angleCls(name)}
                {...HOOD_ANGLE_LIMITS[name]} onChange={onModelField} />
            </div>
          ))}
        </LabelRow>
        <LabelRow label="Material"><SelectModel name="mtl_id" value={model.mtl_id} rows={list(cms, 'mtl')} labelKey="model" onChange={onModelField} /></LabelRow>
        <LabelRow label="Screen"><SelectModel name="screen_id" value={model.screen_id} rows={list(cms, 'screen')} labelKey="model" onChange={onModelField} hideEmpty={false} /></LabelRow>
      </div>
    );
    rightCol = (
      <div className="col-xs-5" style={{ marginTop: columnTopMargin }}>
        <LabelRow label="Air Stream"><SelectModel name="air_stream_id" value={model.air_stream_id} rows={airStream} labelKey="name" onChange={onModelField} hideEmpty={false} /></LabelRow>
        <LabelRow label="Air Direction"><SelectModel name="air_direction_id" value={model.air_direction_id} rows={list(cms, 'air_direction')} labelKey="name" onChange={onModelField} /></LabelRow>
        <LabelRow label="Airflow" unit="CFM"><NumberInput name="airflow_actual" value={model.airflow_actual} formatter="integer" onChange={onModelField} /></LabelRow>
        <LabelRow label="Inlet Face Area" unit="ft2"><NumberInput name="face_area" value={model.face_area} cls="w-100 model_field non-clickable" readOnly /></LabelRow>
        <FaceVelBlock type={type} model={model} onModelField={onModelField} onAutoSize={onAutoSize} />
      </div>
    );
  } else {
    // damper + louver share the manufacturer/series/params column.
    // ★ The three identity selects render the FULL cms lists, in the order the
    // server served them, and the cascade only marks the disallowed options
    // `hide` — `_syncManufacturerSeriesFilters`' mechanism, not a filtered list.
    // Rendering the filtered list instead silently re-ordered the dropdowns
    // (see `syncManufacturerSeriesFilters`).
    const cascade = props.paramsCascade;
    // louver.selector.tmpl:73-95 emits Frame Material BEFORE Blade Orientation,
    // and those two rows are NOT wrapped in `{{#if combo_mode}}` — the order is
    // the same for a plain louver as for a combo (measured row order: 4
    // Manufacturer, 5 Series, 6 Frame Material, 7 Blade Orientation, 8 Available
    // Airway, 9 Selected Airway). React had it right only for a combo and left
    // the plain louver with Frame Material last, after the airway rows.
    // DAMPER is different and keeps its own place: damper.selector.tmpl runs
    // Series → airways → Leakage → Blade Orientation → Shaft Material → Frame
    // Material (:151), so the row stays at the bottom of that column.
    const frameMaterialRow = (
      <LabelRow label="Frame Material"><SelectParams name="frame_mtl_id" value={params.frame_mtl_id} rows={list(cms, 'frame_mtl')} labelKey="name" onChange={onParamsField} hideEmpty hiddenIds={cascade.hiddenFrameMtlIds} /></LabelRow>
    );
    // tmpl:89 — the louver blade-orientation placeholder carries `class="hide"`
    // (measured: legacy 3 options / 2 visible, React was showing all 3).
    // `_updateBladeOrientationSelect` :1924 only removes `option:not([value=""])`,
    // so the hidden placeholder survives every rebuild.
    //
    // ★ The SOURCE list is whichever of TWO competing legacy mechanisms ran last:
    //  (a) the template expression `accessory.selector.js:346`
    //        blade_orientation_options: !params.series_id
    //          ? cms.blade_orientation
    //          : (_louverBladeOrientations || cms.blade_orientation || [])
    //  (b) `_updateBladeOrientationSelect` (:1923-1927), which imperatively
    //      removes `option:not([value=""])` and re-appends the R4-FETCHED list.
    // In COMBO mode an identity pick always calls `getAccessoryData()` (:2489),
    // so a full re-render follows and (a) wins. In NON-COMBO nothing re-renders
    // after the pick, so (b) — the fetched list — is what the user is left with.
    // Both measured on dev3:
    //  · combo, mfg 104 (TAMCO), no series → R4 = [Horizontal] only, yet legacy
    //    still offers Vertical (3 options / 2 visible) = the full CMS pair, with
    //    blade_orientation_id auto-picked to 1 from the single-item FETCHED list.
    //    Using the fetched list here dropped Vertical (2 options / 1 visible) and
    //    made the whole Vertical branch — including the `:576` all-five-frame-
    //    materials fallback — unreachable for such a manufacturer.
    //  · plain louver 13899, pick mfg 47 while series 97 (a Ruskin series) is
    //    still set → R4(mfg=47&series=97) = [] → legacy is left with ONE option,
    //    the hidden placeholder (t1/h1), an empty unusable select. So non-combo
    //    must keep using the fetched list, empty array included — note `??`
    //    deliberately does NOT fall back on `[]`, only on null.
    const bladeOrientationRows = (
      comboMode && !params.series_id
        ? list(cms, 'blade_orientation')
        : ((bladeOrientations as Array<Record<string, unknown>>) ?? list(cms, 'blade_orientation'))
    );
    const louverBladeOrientationRow = (
      <LabelRow label="Blade Orientation"><SelectParams name="blade_orientation_id" value={params.blade_orientation_id}
        rows={bladeOrientationRows} labelKey="name" onChange={onParamsField} hideEmpty /></LabelRow>
    );
    // ★ The damper's Blade Orientation is a RADIO group, and legacy emits it
    // AFTER Leakage, not after Series: `damper.selector.tmpl` runs Tag :18,
    // Manufacturer :25, Series :37, Available Airway :51, Selected Airway :67,
    // Selected OD :92, Leakage :107, Blade Orientation :119, Shaft Material
    // :139, Frame Material :151. The louver's select-based row genuinely does
    // sit right after Series (`louver.selector.tmpl:86`), which is why the two
    // cannot share a slot.
    const damperBladeOrientationRow = (
      <LabelRow label="Blade Orientation">
        <div className="checkboxes-group full-width">
          {list(cms, 'blade_orientation').map((r) => (
            <label className="radio" key={String(r.id)}>
              <input type="radio" name="blade_orientation_id" value={String(r.id)} className="params_field"
                checked={eq(r.id, params.blade_orientation_id)} onChange={onParamsField} />
              <span>{String(r.name ?? '')}</span>
            </label>
          ))}
        </div>
      </LabelRow>
    );
    leftCol1 = (
      <div className="col-xs-5">
        {smartlistRow}
        {/* tmpl:18-26 — combo-only *How to*, above Tag. */}
        {comboMode && <ComboHowToRow onHowTo={props.onComboHowTo} />}
        <div className="row tag-row"><div className="col-xs-5 label-part"><span>Tag</span></div>
          <div className="col-xs-7 input-part"><input name="tag" type="text" className="model_field w-100" maxLength={36} value={dec(model.tag)} onChange={onModelField} /></div></div>
        {/* tmpl:34-47 — combo-only "Louvers x / y · Reset · Smartlist", below Tag. */}
        {comboMode && (
          <ComboResetRow counts={comboCounts} onSmartlist={props.onComboSmartlistToggle}
            onReset={props.onComboReset} />
        )}
        <LabelRow label="Manufacturer"><SelectParams name="mfg_id" value={params.mfg_id} rows={list(cms, 'manufacturer')} labelKey="name" onChange={onParamsField} hiddenIds={cascade.hiddenMfgIds} /></LabelRow>
        <LabelRow label="Series"><SelectParams name="series_id" value={params.series_id} rows={list(cms, 'series')} labelKey="series" onChange={onParamsField} hiddenIds={cascade.hiddenSeriesIds} /></LabelRow>
        {type === 'louver' && frameMaterialRow}
        {type === 'louver' && louverBladeOrientationRow}
        {/* Combo Louver — BOTH airway blocks are hidden for a combo:
            *Available Airway* tmpl:99-116 `{{#unless combo_mode}}` and
            *Selected Airway W x H* tmpl:118-143 `{{#unless combo_mode}}`. A combo
            has no single airway pair — `aw_width`/`aw_height` come off the (null)
            `master_louver` and serialize as null; the sizes are PER-FACE below
            (inventory §1 table + §2.2 rows marked "hidden in combo"). */}
        {!comboMode && <AvailableAirway awDims={awDims} />}
        {!comboMode && (
          <div className="row"><div className="col-xs-5 label-part"><span>Selected Airway W x H</span><span className="unit right">in</span></div>
            <div className="col-xs-7 input-part row">
              <div className="col-xs-6">
                <select name="aw_width" className="params_field" value={params.aw_width == null ? '' : String(params.aw_width)} onChange={onParamsField}>
                  <option value="">- -</option>
                  {list(cms, 'aw_width').map((r) => <option key={String(r.aw_width)} value={String(r.aw_width)}>{trunc(r.aw_width)}</option>)}
                </select>
              </div>
              <div className="col-xs-6">
                <select name="aw_height" className="params_field" value={params.aw_height == null ? '' : String(params.aw_height)} onChange={onParamsField}>
                  <option value="">- -</option>
                  {list(cms, 'aw_height').map((r) => <option key={String(r.aw_height)} value={String(r.aw_height)}>{trunc(r.aw_height)}</option>)}
                </select>
              </div>
            </div></div>
        )}
        {type === 'damper' && (
          <div className="row"><div className="col-xs-5 label-part"><span>Selected OD W x H</span><span className="unit right">in</span></div>
            <div className="col-xs-7 input-part row">
              <div className="col-xs-6"><NumberInput name="od_width" value={trunc(model.od_width)} cls="" readOnly /></div>
              <div className="col-xs-6"><NumberInput name="od_height" value={trunc(model.od_height)} cls="" readOnly /></div>
            </div></div>
        )}
        {type === 'damper' && <LabelRow label="Leakage"><SelectParams name="leakage_id" value={params.leakage_id} rows={list(cms, 'leakage')} labelKey="name" onChange={onParamsField} hideEmpty /></LabelRow>}
        {type === 'damper' && damperBladeOrientationRow}
        {type === 'damper' && <LabelRow label="Shaft Material"><SelectParams name="shaft_mtl_id" value={params.shaft_mtl_id} rows={list(cms, 'shaft_mtl')} labelKey="name" onChange={onParamsField} hideEmpty /></LabelRow>}
        {type === 'damper' && frameMaterialRow}
        {/* tmpl:145-170 — the three per-face airway rows, then tmpl:171-181 —
            Casing Mount relocated into THIS column for a combo. */}
        {comboMode && (
          <ComboFaceRows facesView={comboFacesView} onToggle={props.onComboFaceToggle}
            onField={props.onComboFaceField} />
        )}
        {comboMode && (
          <ComboCasingMountRow rows={list(cms, 'casing_mount')} current={model.casing_mount_id}
            onModelField={onModelField} />
        )}
      </div>
    );
    rightCol = (
      <div className="col-xs-5" style={{ marginTop: columnTopMargin }}>
        <LabelRow label="Air Stream"><SelectModel name="air_stream_id" value={model.air_stream_id} rows={airStream} labelKey="name" onChange={onModelField} hideEmpty={false} /></LabelRow>
        <LabelRow label="Air Direction"><SelectModel name="air_direction_id" value={model.air_direction_id} rows={list(cms, 'air_direction')} labelKey="name" onChange={onModelField} /></LabelRow>
        <LabelRow label="Airflow" unit="CFM"><NumberInput name="airflow_actual" value={model.airflow_actual} formatter="integer" onChange={onModelField} /></LabelRow>
        <FaceVelBlock type={type} model={model} onModelField={onModelField} onAutoSize={onAutoSize}
          comboMode={comboMode} comboAutoSizeEnabled={comboAutoSizeEnabled}
          onComboAutoSizeToggle={props.onComboAutoSizeToggle} />
      </div>
    );
  }

  const dialogClass = `coil_new coil-rating-mode accessory_selector_dialog ${type}_selector_dialog input-part row`;
  return (
    <div id="coil_new" className={dialogClass}>
      <div className="col-xs-12 content-data tab-content">
        <div className="tab-pane input-tab-pane in active scrollable-content float-left" id={`${type}_Selection`}>
          {leftCol1}
          <div className="col-xs-7 set-min-height" />
          {rightCol}
          {positionBlock}
        </div>
      </div>
    </div>
  );
};

export type SmartlistState = {
  items: any[];
  count: number | null;
  cmsCount: number | null;
  /** see the `smartlist` state in AccessorySelector.tsx — 'initial' is the
   * page-1 fetch (centre spinner INSTEAD of the rows), 'more' the
   * infinite-scroll append (rows kept, bottom spinner after them). */
  loading: 'none' | 'initial' | 'more';
};

/** Smartlist rows (damper/louver) — rendered into the right-panel flip-back by
 * the parent selector; the row markup matches legacy updateSmartlistDOM. */
export const SmartlistRows = ({ type, smartlist, selectedId, onRow, onScroll }: {
  type: AccType;
  smartlist: SmartlistState;
  selectedId: unknown;
  onRow: (id: number) => void;
  /** keeps `ul.list-heading` in step when the list is scrolled sideways, and
   * drives infinite scroll — the heading is a sibling, so it has no scroll box
   * in common with these rows. */
  onScroll?: (e: React.UIEvent<HTMLUListElement>) => void;
}) => {
  if (type !== 'damper' && type !== 'louver') return null;
  const whCol = type === 'damper' ? 'w-70' : 'w-80';
  // ★ legacy `loadSmartlist` :3034-3036 — for the whole of the page-1 fetch the
  // rows are gone and a centre spinner stands in their place, so the user never
  // reads a pending request as "No results". Styles are already in
  // common.css:19894-19902 (shared with the Fan VFD mount smartlist).
  if (smartlist.loading === 'initial') {
    return (
      <ul className="list-rows scrollable catalogue-rows-container smartlist-items" tabIndex={0}>
        <li className="smartlist-center-spinner">
          <div className="preloader dotted-preloader active" />
        </li>
      </ul>
    );
  }
  return (
    <ul className="list-rows scrollable catalogue-rows-container smartlist-items" tabIndex={0}
      onScroll={onScroll}>
      {smartlist.items.length === 0 && <li className="smartlist-no-results">No results</li>}
      {smartlist.items.map((item) => (
        <li key={item.id} className={`smartlist-row${item.id === selectedId ? ' synced' : ''}`} data-id={item.id}
          onClick={() => onRow(item.id)}>
          <ul className={`table-item ${type}`}>
            <li className="xxlong text-overflow-unset">
              <span className={`only-for-${type}-row custom-columns-row true`}>
                <span className="w-200">{item.mfg_name ?? ''}</span>
                <span className="w-100">{item.series_name ?? ''}</span>
                {type === 'damper' && <span className="w-60">{item.blade_orientation_name ?? ''}</span>}
                {/* w-70 on damper, legacy's w-80 on louver — see `whCol` in
                    AccessorySelector.tsx, which sets the matching <h4>s. */}
                <span className={`${whCol} text-align-right`}>{trunc(item.aw_width)}</span>
                <span className={`${whCol} text-align-right`}>{trunc(item.aw_height)}</span>
                {/* FRAME — see the matching <h4> in AccessorySelector.tsx; not legacy. */}
                {type === 'damper' && <span className="smartlist-frame-col">{item.frame_mtl_name ?? ''}</span>}
              </span>
            </li>
          </ul>
        </li>
      ))}
      {/* legacy `loadMoreSmartlist` :3148-3150 appends this `li` while the next
          page is in flight and removes it on arrival; the rows above stay put.
          Styled at common.css:19903-19918. */}
      {smartlist.loading === 'more' && <li className="smartlist-bottom-spinner" />}
    </ul>
  );
};

/**
 * COMBO LOUVER (KA-5245) — A1.2: the SHARED combo smartlist rows
 * (louver.selector.tmpl:676-690), rendered into the right-panel flip-back.
 *
 * A combo row is a catalog PRODUCT LINE — manufacturer / series / frame material /
 * blade orientation — with NO sizes, because all three faces share one identity
 * and only the sizes are per-face. 43 distinct rows on dev3.
 *  · rendered STATICALLY out of `cms.combo_smartlist` (already narrowed by
 *    `_comboSmartlistFilteredItems`), never by the per-part loader — Q12: legacy's
 *    `loadSmartlist` / `updateSmartlistDOM` / `appendSmartlistItems` /
 *    `loadMoreSmartlist` ALL early-return on combo mode, so there is no infinite
 *    scroll and no pagination here;
 *  · `synced` requires series_id AND frame_mtl_id to match the current params —
 *    the template's nested `ifCompare` pair, NOT an id equality like the plain row;
 *  · the "No results" li mirrors `{{#unless combo_smartlist_items}}` (:690-692).
 * The click handler is `_applyComboSmartlistPick` via `selectFromSmartlist`
 * :3316-3319 — it sets the shared identity for all three faces and never touches
 * `master_louver_id` (a combo's is always null).
 */
export const ComboSmartlistRows = ({ items, params, onRow, onScroll }: {
  items: ComboSmartlistRow[];
  params: Record<string, unknown>;
  onRow: (row: ComboSmartlistRow) => void;
  /** see `SmartlistRows` — the heading is a sibling and has to be dragged along. */
  onScroll?: (e: React.UIEvent<HTMLUListElement>) => void;
}) => (
  <ul className="list-rows scrollable catalogue-rows-container smartlist-items" tabIndex={0}
    onScroll={onScroll}>
    {items.length === 0 && <li className="smartlist-no-results">No results</li>}
    {items.map((r, i) => {
      const synced = eq(r.series_id, params.series_id) && eq(r.frame_mtl_id, params.frame_mtl_id);
      return (
        <li
          key={`${r.mfg_id}_${r.series_id}_${r.blade_orientation_id}_${r.frame_mtl_id}_${i}`}
          className={`smartlist-row combo-smartlist-row ${synced ? 'synced' : ''}`}
          data-mfg={r.mfg_id as number}
          data-series={r.series_id as number}
          data-blade={r.blade_orientation_id as number}
          data-frame={r.frame_mtl_id as number}
          onClick={() => onRow(r)}
        >
          <ul className="table-item louver">
            <li className="xxlong text-overflow-unset">
              <span className="only-for-louver-row custom-columns-row true">
                <span className="w-160">{String(r.mfg_name ?? '')}</span>
                <span className="w-100">{String(r.series_name ?? '')}</span>
                <span className="w-180">{String(r.frame_mtl_name ?? '')}</span>
                <span className="w-120">{String(r.blade_orientation_name ?? '')}</span>
              </span>
            </li>
          </ul>
        </li>
      );
    })}
  </ul>
);

export default AccessoryLeftPanel;
